import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";
import { THEME_BACKGROUNDS, HERO_DIM_OPACITY } from "@/constants/theme";
import HeroThemeVideo from "@/components/HeroThemeVideo";

export default function HeroSection() {
  const { t } = useTranslation();
  const { isDark, setCinematicAvailable, heroVideoActive } = useTheme();
  const sectionRef = useRef(null);

  // The cinematic video + button are only available while the hero background
  // is meaningfully on screen (i.e. the "top part" of the landing page).
  // We watch how much of the hero section is visible and report it to context.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setCinematicAvailable(true); // safe fallback
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Available when at least ~55% of the hero (its background image) is visible.
        setCinematicAvailable(entry.isIntersecting && entry.intersectionRatio >= 0.55);
      },
      { threshold: [0, 0.25, 0.55, 0.8, 1] }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      setCinematicAvailable(false); // hero unmounted (navigated away)
    };
  }, [setCinematicAvailable]);

  return (
    <section 
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={isDark ? THEME_BACKGROUNDS.dark : THEME_BACKGROUNDS.light}
          alt="Luxury car showroom"
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 ${isDark ? 'hero-overlay' : 'hero-overlay-light'}`} />
      </div>

      {/* Radial Gradient Overlay */}
      <div className={`absolute inset-0 ${
        isDark 
          ? 'bg-gradient-to-b from-[#1E40AF]/20 via-transparent to-[#050505]' 
          : 'bg-gradient-to-b from-red-600/20 via-white/40 to-[#FAFAFA]'
      }`} />

      {/* Cinematic transition video — sits over the background image, below content */}
      <HeroThemeVideo />

      {/* Content (dimmed — not hidden — while the transition video plays) */}
      <motion.div
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32"
        animate={{ opacity: heroVideoActive ? HERO_DIM_OPACITY : 1 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
      >
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className={`inline-block px-4 py-2 backdrop-blur-sm border text-sm uppercase tracking-widest font-medium mb-6 ${
              isDark 
                ? 'bg-white/10 border-white/20 text-blue-400' 
                : 'bg-red-600/90 border-red-700 text-white'
            }`}>
              {t('hero.tagline')}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-unbounded text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-6"
          >
            {t('hero.title')}
            <span className={`block ${isDark ? 'text-gray-400' : 'text-gray-200'}`}>{t('hero.subtitle')}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className={`text-lg md:text-xl mb-10 max-w-xl leading-relaxed ${
              isDark ? 'text-gray-300' : 'text-gray-200'
            }`}
          >
            {t('hero.description')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link to="/inventory">
              <Button
                size="lg"
                className={`rounded-none px-8 py-6 font-semibold text-sm uppercase tracking-wider group ${
                  isDark 
                    ? 'bg-white text-black hover:bg-gray-200' 
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
                data-testid="hero-explore-btn"
              >
                {t('hero.exploreBtn')}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            <a href="#contact">
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 rounded-none px-8 py-6 font-semibold text-sm uppercase tracking-wider"
                data-testid="hero-contact-btn"
              >
                {t('hero.scheduleBtn')}
              </Button>
            </a>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-20 grid grid-cols-3 gap-6 max-w-2xl"
        >
          <div className={`text-center border-r pr-6 ${isDark ? 'border-white/10' : 'border-gray-900/20'}`}>
            <div className={`font-unbounded text-3xl md:text-4xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>10,000+</div>
            <div className={`text-xs md:text-sm uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{t('hero.vehiclesSold')}</div>
          </div>
          <div className={`text-center border-r px-6 ${isDark ? 'border-white/10' : 'border-gray-900/20'}`}>
            <div className={`font-unbounded text-3xl md:text-4xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>98%</div>
            <div className={`text-xs md:text-sm uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{t('hero.satisfaction')}</div>
          </div>
          <div className="text-center pl-6">
            <div className={`font-unbounded text-3xl md:text-4xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>8+</div>
            <div className={`text-xs md:text-sm uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{t('hero.yearsExperience')}</div>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-white rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
}
